import React, { Suspense } from 'react';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter, Routes, Route, Navigate, useRoutes, useNavigate } from 'react-router-dom';
import { store, persistor } from './redux/store';
import { keyframesCss, colors, fonts } from './theme/tokens';
import { selectIsLoggedIn, selectAccessToken, selectMustChangePassword } from './redux/auth/auth.selectors';
import type { RootState } from './redux/root-reducer';
import { selectDataLoaded, selectDataLoading } from './redux/data/data.selectors';
import { dataLoadStart } from './redux/data/data.action';
import { authLogout, authLoginFailure, authTokensRefreshed } from './redux/auth/auth.action';
import { setAccessToken, setRefreshToken, setRefreshedHandler, setUnauthorizedHandler } from './redux/_common/tokenHolder';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/login/LoginPage';
import ChangePasswordPage from './pages/account/ChangePasswordPage';
import { contentRoutes } from './routes/contentRoutes';

/* Renders the authenticated route table inside the shell. */
const AuthenticatedApp: React.FC = () => {
  const element = useRoutes([
    ...contentRoutes,
    { path: '*', element: <Navigate to="/dashboard" replace /> },
  ]);
  return (
    <AppShell>
      <Suspense fallback={<div style={{ padding: 40, color: colors.muted }}>Loading…</div>}>
        {element}
      </Suspense>
    </AppShell>
  );
};

const Root: React.FC = () => {
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const mustChangePassword = useSelector(selectMustChangePassword);
  const persistedToken = useSelector(selectAccessToken);
  const dataLoaded = useSelector(selectDataLoaded);
  const dataLoading = useSelector(selectDataLoading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Re-hydrate the in-memory token holder from the persisted auth slice on boot,
  // and register the 401 handler (interceptor → logout + redirect to '/').
  const persistedRefresh = useSelector((st: RootState) => st.auth.refreshToken);
  React.useEffect(() => {
    setAccessToken(persistedToken);
  }, [persistedToken]);
  React.useEffect(() => {
    setRefreshToken(persistedRefresh);
  }, [persistedRefresh]);
  React.useEffect(() => {
    setRefreshedHandler((t) => dispatch(authTokensRefreshed(t)));
    return () => setRefreshedHandler(null);
  }, [dispatch]);

  // Load reference data once a session exists — fires when isLoggedIn flips on
  // login AND on a hard refresh (auth is persisted, the data slice is not).
  // Don't even fetch reference data while a password change is pending — the
  // API refuses every endpoint but the change itself, so the load would only
  // produce a wall of 403s behind the screen.
  React.useEffect(() => {
    if (isLoggedIn && !mustChangePassword && !dataLoaded && !dataLoading) {
      dispatch(dataLoadStart());
    }
  }, [isLoggedIn, mustChangePassword, dataLoaded, dataLoading, dispatch]);

  React.useEffect(() => {
    setUnauthorizedHandler((reason) => {
      dispatch(authLogout());
      // Say why on the sign-in page, rather than dropping the user there cold.
      if (reason) dispatch(authLoginFailure({ message: reason.message, code: reason.code }));
      navigate('/', { replace: true });
    });
    return () => setUnauthorizedHandler(null);
  }, [dispatch, navigate]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          isLoggedIn ? (
            <Navigate to={mustChangePassword ? '/account/password' : '/dashboard'} replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/*"
        element={
          !isLoggedIn ? (
            <Navigate to="/" replace />
          ) : mustChangePassword ? (
            /* Nothing else is reachable until the password is set. */
            <ChangePasswordPage />
          ) : (
            <AuthenticatedApp />
          )
        }
      />
    </Routes>
  );
};

export default function App(): JSX.Element {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <style>{keyframesCss}</style>
        <div style={{ fontFamily: fonts.body, color: colors.ink }}>
          <BrowserRouter>
            <Root />
          </BrowserRouter>
        </div>
      </PersistGate>
    </Provider>
  );
}
